import { IsAdhesiveUsed, RemovabilityConditionId, RemovedBeforeUsage, RemovedByConsumer } from '../../../services/component-services/field-name-constants';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { RemovabilityConditionDto } from 'src/app/data-transfer/entities/removability-condition-dto';
import { PackagingComponentTypesEnum } from 'src/app/model/packaging-component-types-enum';
import { GtinCheckService } from 'src/app/services/component-services/gtin-check-service';
import { ComponentTypeService } from 'src/app/navigation/services/component-type-service';
import { TranslocoService } from '@jsverse/transloco';
import { ComponentSubtypeDto } from 'src/app/data-transfer/entities/component-subtype-dto';
import { CountryDto } from 'src/app/data-transfer/entities/country-dto';
import { MaterialManifestationDto } from 'src/app/data-transfer/entities/material-manifestation-dto';
import { MaterialFunctionDto } from 'src/app/data-transfer/entities/material-function-dto';
import { ColorDto } from 'src/app/data-transfer/entities/color-dto';
import { CompositeMaterialSevice } from 'src/app/services/material-services/composite-material-service';
import { MultiMaterialCompositeDto } from 'src/app/data-transfer/entities/material-entities/multi-material-composite-dto';
import { Subscription } from 'rxjs';
import { PackagingComponentPrototype } from 'src/app/model/components/packaging-component-prototype';
import { RelevantAnalysisTypes } from 'src/app/util/analyses-util/relevant-analysis-service';

@Component({
  selector: 'app-component-edit',
  templateUrl: './component-edit.component.html',
  styleUrls: ['./component-edit.component.scss']
})
export class ComponentEditComponent implements OnInit, OnChanges, OnDestroy {

  @Input({ required: true }) packagingComponentType!: PackagingComponentTypesEnum;
  @Input({ required: true }) packagingUnitTypeId!: number;
  @Input({ required: true }) canEditForm!: boolean;
  @Input({ required: true }) isUserValidator!: boolean;
  @Input({ required: true }) isPreview!: boolean;
  @Input({ required: true }) isTracked!: boolean;
  @Input({ required: true }) allColors!: ColorDto[];
  @Input({ required: true }) allMaterialFunctions!: MaterialFunctionDto[];
  @Input({ required: true }) allManifestations!: MaterialManifestationDto[];
  @Input({ required: true }) manufacturingCountries!: CountryDto[];
  @Input({ required: true }) baseForm!: FormGroup;
  @Input({ required: true }) componentSubtypes!: ComponentSubtypeDto[];
  @Input({ required: true }) removabilityConditions!: RemovabilityConditionDto[];
  @Input({ required: true }) componentPrototype!: PackagingComponentPrototype;
  @Input()
    get relevantAnalysisTypes(): RelevantAnalysisTypes {
      return this._relevantAnalysisTypes;
    }
    set relevantAnalysisTypes(newValue: RelevantAnalysisTypes) {
      this._relevantAnalysisTypes = newValue;
      this.relevantAnalysisTypesChange.emit(newValue);
    }
  @Output() relevantAnalysisTypesChange = new EventEmitter();

  addEditComponentForm!: FormGroup;

  selectedManufacturingCountry!: string;
  callerId = -1;
  compositeMaterial!: MultiMaterialCompositeDto;
  compositeMaterialBackup!: MultiMaterialCompositeDto;
  hasMaterialChanged = false;
  isNew = false;
  private _relevantAnalysisTypes: RelevantAnalysisTypes = new RelevantAnalysisTypes(false,false,false)
  private totalGrammageSubscription?: Subscription;
  private gtinSubscription?: Subscription;
  private manufCountrySubscription?: Subscription;
  private typeSubscription?: Subscription;

  get isDecoration() {
    return this.packagingComponentType === PackagingComponentTypesEnum.Decoration;
  }

  get isMainBody() {
    return this.packagingComponentType === PackagingComponentTypesEnum.MainBody;
  }

  get isInlay() {
    return this.packagingComponentType === PackagingComponentTypesEnum.Inlay;
  }

  get componentTypeSelectionLabel() {
    if (this.packagingComponentType === PackagingComponentTypesEnum.MainBody) {
      return this.translationService.translate('component.fields.ofMainBody');
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.Closure) {
      return this.translationService.translate('component.fields.ofClosure');
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.Decoration) {
      return this.translationService.translate('component.fields.ofDecoration');
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.Inlay) {
      return this.translationService.translate('component.fields.ofInlay');
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.PackagingAid) {
      return this.translationService.translate('component.fields.ofPackagingAid');
    }
    return '';
  }

  constructor(
    private gtinCheckService: GtinCheckService,
    private componentTypeService: ComponentTypeService,
    private translationService: TranslocoService
  ) { }

  ngOnInit(): void {
    if (this.packagingComponentType === PackagingComponentTypesEnum.MainBody) {
      this.callerId = PackagingComponentTypesEnum.MainBody;
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.Closure) {
      this.callerId = PackagingComponentTypesEnum.Closure;
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.Decoration) {
      this.callerId = PackagingComponentTypesEnum.Decoration;
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.Inlay) {
      this.callerId = PackagingComponentTypesEnum.Inlay;
    }
    if (this.packagingComponentType === PackagingComponentTypesEnum.PackagingAid) {
      this.callerId = PackagingComponentTypesEnum.PackagingAid;
    }

    this.isNew = this.baseForm.value.id ? false : true;

    this.setCommonComponentData();

    this.addEditComponentForm = this.baseForm;
    this.subscribeToValueChanges(this.addEditComponentForm);

    this.checkDecorationTypeDisableFields(this.addEditComponentForm.controls.packagingComponentSubtypeId.value,
      this.addEditComponentForm.controls.isAdhesiveUsed?.value);
    if (!this.canEditForm) {
      this.addEditComponentForm.disable();
    }
    this.selectedManufacturingCountry = this.addEditComponentForm.controls.manufacturingCountry.value ?? '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.canEditForm) {
      if (!this.canEditForm && this.addEditComponentForm != null) {
        this.addEditComponentForm.disable();
      }
    }
  }

  ngOnDestroy(): void {
    this.totalGrammageSubscription?.unsubscribe();
    this.gtinSubscription?.unsubscribe();
    this.manufCountrySubscription?.unsubscribe();
    this.typeSubscription?.unsubscribe();
  }

  onComponentTypeChange(componentType: number) {
    if (this.packagingComponentType === PackagingComponentTypesEnum.Decoration) {
      this.onDecorationTypeChange(componentType);
    }
  }

  onDecorationTypeChange(decoType: number) {
    this.checkDecorationTypeDisableFields(decoType, this.addEditComponentForm.get(IsAdhesiveUsed)?.value);
  }

  onAdhesivesOptionChange(isAdhesiveUsed: boolean) {
    this.checkDecorationTypeDisableFields(this.addEditComponentForm.controls.packagingComponentSubtypeId.value, isAdhesiveUsed);
  }

  materialChanged(hasMaterialChanged: boolean) {
    if (hasMaterialChanged) {
      this.addEditComponentForm.controls.multiMaterial.patchValue(this.compositeMaterial);
      this.addEditComponentForm.controls.multiMaterial.markAsDirty();
      this.hasMaterialChanged = true;
      this.componentPrototype.setComponentSpecificTableData(this.addEditComponentForm);
    }
  }

  totalWeightChanged(newTotalWeight: number, form: FormGroup): void {
    form.controls.totalWeight.patchValue(newTotalWeight);
    this.compositeMaterial.totalWeight = newTotalWeight;
  }

  private setCommonComponentData() {
    const originalMaterial = this.baseForm.value.multiMaterial ?? CompositeMaterialSevice.createEmptyCompositeMaterial();
    this.compositeMaterial = JSON.parse(JSON.stringify(originalMaterial));
    this.compositeMaterialBackup = JSON.parse(JSON.stringify(originalMaterial));
  }

  private checkDecorationTypeDisableFields(type: number, adhesiveUsed: boolean) {
    const removedBeforeUsage = this.addEditComponentForm.get(RemovedBeforeUsage)?.value ?? false;

    if (type == null || this.componentTypeService.isDecorationOfTypeSleeve(type)) {
      this.addEditComponentForm.get(IsAdhesiveUsed)?.disable();
      this.addEditComponentForm.get(RemovabilityConditionId)?.disable();
      this.addEditComponentForm.get(RemovedBeforeUsage)?.enable();
      if (!removedBeforeUsage) {
        this.addEditComponentForm.get(RemovedByConsumer)?.enable();
      } else {
        this.addEditComponentForm.get(RemovedByConsumer)?.disable();
        this.addEditComponentForm.get(RemovedByConsumer)?.patchValue(true);
      }
      this.addEditComponentForm.get(IsAdhesiveUsed)?.patchValue(null);
      this.addEditComponentForm.get(RemovabilityConditionId)?.patchValue(null);
    }

    if (this.componentTypeService.isDecorationOfTypeLabel(type) || this.componentTypeService.isDecorationOfTypeInMold(type)) {
      this.addEditComponentForm.get(IsAdhesiveUsed)?.enable();
      this.addEditComponentForm.get(RemovedBeforeUsage)?.enable();
      if (!removedBeforeUsage) {
        this.addEditComponentForm.get(RemovedByConsumer)?.enable();
      } else {
        this.addEditComponentForm.get(RemovedByConsumer)?.disable();
        this.addEditComponentForm.get(RemovedByConsumer)?.patchValue(true);
      }
      if (adhesiveUsed) {
        this.addEditComponentForm.get(RemovabilityConditionId)?.enable();
      } else {
        this.addEditComponentForm.get(RemovabilityConditionId)?.disable();
        this.addEditComponentForm.get(RemovabilityConditionId)?.patchValue(null);
      }
    }
  }

  private subscribeToValueChanges(form: FormGroup) {
    this.totalGrammageSubscription = form.controls.totalGrammage.valueChanges.subscribe(newTotalGrammage =>
      this.compositeMaterial.totalGrammage = newTotalGrammage);
    this.gtinSubscription = form.controls.gtin.valueChanges.subscribe(async val => {
      form.controls.gtin.setValue(val?.trim(), { emitEvent: false });
      await this.gtinCheckService.checkGtinValidity(form);
      form.controls.gtin.markAsTouched();
    });
    this.manufCountrySubscription = form.controls.manufacturingCountry.valueChanges.subscribe(newCountry =>
      this.selectedManufacturingCountry = newCountry);
  }
}
